import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import api, { formatApiError } from '../lib/api';
import { Check, CreditCard, Lightning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const LIMIT_ROWS = [
  ['max_discovery_jobs', 'billing.jobs'],
  ['max_companies', 'billing.companies'],
  ['max_contacts', 'billing.contacts'],
  ['max_exports', 'billing.exports'],
  ['max_export_records', 'billing.exportRecords'],
  ['max_seats', 'billing.seats'],
  ['max_pushed_leads', 'billing.pushes'],
];

export default function BillingPage() {
  const { user, checkAuth } = useSaaSAuth();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(user?.entitlements || null);
  const [accountUsage, setAccountUsage] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const locale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const intlLocale = locale === 'de' ? 'de-DE' : locale === 'es' ? 'es-ES' : 'en-GB';

  useEffect(() => {
    let active = true;
    Promise.all([api.get('/saas/plans'), api.get('/billing/status'), api.get('/saas/usage')])
      .then(([planResponse, statusResponse, usageResponse]) => {
        if (active) {
          setPlans(planResponse.data);
          setStatus(statusResponse.data);
          setAccountUsage(usageResponse.data);
        }
      })
      .catch((error) => toast.error(formatApiError(error)));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      toast.success(t('billing.checkoutSuccess'));
      checkAuth();
      api.get('/billing/status').then((response) => setStatus(response.data)).catch(() => {});
    } else if (checkout === 'cancelled') {
      toast.info(t('billing.checkoutCancelled'));
    }
  }, [checkAuth, searchParams, t]);

  const billedName = status?.billed_plan?.name || '';
  const currentLimits = status?.limits || {};
  const currentUsage = status?.usage || {};
  const subscription = status?.subscription || {};
  const canManage = ['account_owner', 'admin', 'super_admin'].includes(user?.role);

  const formatPrice = useMemo(() => new Intl.NumberFormat(intlLocale, {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }), [intlLocale]);

  const displayDate = (value) => value ? new Date(value).toLocaleDateString(intlLocale) : '';
  const displayLimit = (value) => Number(value) === -1 ? t('common.unlimited') : Number(value || 0).toLocaleString(intlLocale);
  const displayStatus = subscription.status
    ? t(`billing.statuses.${subscription.status}`, { defaultValue: subscription.status })
    : '—';

  const handleCheckout = async (planName) => {
    setLoadingPlan(planName);
    try {
      const { data } = await api.post('/billing/checkout', {
        plan_name: planName,
        billing_cycle: yearly ? 'yearly' : 'monthly',
        locale,
      });
      if (!data?.checkout_url) throw new Error(t('billing.checkoutError'));
      window.location.assign(data.checkout_url);
    } catch (error) {
      toast.error(formatApiError(error) || t('billing.checkoutError'));
      setLoadingPlan(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/billing/portal');
      window.location.assign(data.portal_url);
    } catch (error) {
      toast.error(formatApiError(error) || t('billing.portalError'));
      setPortalLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground mb-1">{t('billing.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('billing.subtitle')}</p>
      </div>

      <div className="rounded-lg border border-border bg-background p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={18} weight="fill" />
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{t('billing.currentPlan')}</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">{t('billing.billedPlan')}</p>
                <h2 className="font-heading text-xl font-bold" data-testid="billed-plan-name">{status?.billed_plan?.display_name || '—'}</h2>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('billing.effectivePlan')}</p>
                <h2 className="font-heading text-xl font-bold" data-testid="effective-plan-name">{status?.effective_plan?.display_name || '—'}</h2>
              </div>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('billing.status')}: <span className="font-mono text-foreground">{displayStatus}</span>
              {subscription.status === 'trialing' && subscription.trial_ends_at && <> · {t('billing.trialEnds', { date: displayDate(subscription.trial_ends_at) })}</>}
              {subscription.current_period_end && subscription.status === 'active' && <> · {t('billing.periodEnds', { date: displayDate(subscription.current_period_end) })}</>}
            </p>
            {status?.override?.active && <p className="mt-2 text-xs font-semibold text-violet-700 dark:text-violet-300">{t('billing.adminOverride')}: {status.override.reason}</p>}
          </div>
          {subscription.stripe_subscription_id && canManage && (
            <Button variant="outline" onClick={handlePortal} disabled={portalLoading} data-testid="manage-billing-button">
              <CreditCard size={16} className="mr-2" />{t('billing.manageBilling')}
            </Button>
          )}
        </div>

        <div className="mt-6 grid gap-4 border-t border-border pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <UsageMeter label={t('billing.usageDiscovery')} used={currentUsage.discovery_jobs || 0} max={currentLimits.max_discovery_jobs} locale={intlLocale} unlimited={t('common.unlimited')} />
          <UsageMeter label={t('billing.usageCompanies')} used={accountUsage?.companies ?? currentUsage.companies_created ?? 0} max={currentLimits.max_companies} locale={intlLocale} unlimited={t('common.unlimited')} />
          <UsageMeter label={t('billing.usageExports')} used={currentUsage.exports ?? accountUsage?.exports ?? 0} max={currentLimits.max_exports} locale={intlLocale} unlimited={t('common.unlimited')} />
          <UsageMeter label={t('billing.usageSeats')} used={accountUsage?.seats || 1} max={currentLimits.max_seats} locale={intlLocale} unlimited={t('common.unlimited')} />
        </div>
      </div>

      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold">{t('billing.choosePlan')}</h2>
          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/50 p-1">
            <button onClick={() => setYearly(false)} className={`rounded px-3 py-1 text-xs font-medium ${!yearly ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>{t('billing.monthly')}</button>
            <button onClick={() => setYearly(true)} className={`rounded px-3 py-1 text-xs font-medium ${yearly ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>{t('billing.yearly')} <span className="ml-1 text-emerald-600">{t('billing.saveYearly')}</span></button>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {plans.map((plan) => {
            const isCurrent = subscription.status === 'active' && billedName === plan.name;
            const total = yearly ? plan.price_yearly : plan.price_monthly;
            const monthlyEquivalent = yearly ? Number(total) / 12 : Number(total);
            return (
              <article key={plan.id} className={`flex flex-col rounded-lg border p-5 ${isCurrent ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-background'}`} data-testid={`plan-card-${plan.name}`}>
                <div className="mb-1 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">{plan.display_name}</h3>
                  {isCurrent && <span className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-mono uppercase text-primary">{t('common.current')}</span>}
                </div>
                <p className="mb-3 min-h-8 text-xs text-muted-foreground">{t(`billing.planDescriptions.${plan.name}`, { defaultValue: plan.description })}</p>
                <div className="mb-4"><span className="font-heading text-2xl font-bold">{formatPrice.format(monthlyEquivalent)}</span><span className="ml-1 text-xs text-muted-foreground">{t('common.perMonth')}</span></div>
                <Button onClick={() => subscription.stripe_subscription_id ? handlePortal() : handleCheckout(plan.name)} disabled={!canManage || isCurrent || Boolean(loadingPlan) || portalLoading} variant={isCurrent ? 'outline' : 'default'} data-testid={`plan-checkout-${plan.name}`}>
                  {isCurrent ? t('billing.currentPlanButton') : subscription.stripe_subscription_id ? t('billing.manageBilling') : loadingPlan === plan.name ? t('billing.openingCheckout') : t('billing.checkout')}
                </Button>
                <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  {LIMIT_ROWS.map(([field, label]) => (
                    <li key={field} className="flex items-start gap-1.5"><Check size={12} className="mt-0.5 shrink-0 text-emerald-600" /> {displayLimit(plan[field])} {t(label)}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">{t('billing.priceNote')}</p>
      </section>
    </div>
  );
}

function UsageMeter({ label, used, max, locale, unlimited }) {
  const maxValue = Number(max ?? 0);
  const isUnlimited = maxValue === -1;
  const percent = isUnlimited || maxValue <= 0 ? 0 : Math.min(100, Math.round((used / maxValue) * 100));
  const color = percent > 90 ? 'bg-red-500' : percent > 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="text-xs font-mono">{Number(used).toLocaleString(locale)} / {isUnlimited ? unlimited : maxValue.toLocaleString(locale)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className={`h-full ${color}`} style={{ width: isUnlimited ? '10%' : `${percent}%` }} /></div>
    </div>
  );
}
